import { useState, useRef, useCallback } from 'react';

interface UseAsyncSearchOptions<T> {
  searchFn: (query: string, signal: AbortSignal) => Promise<T[]>;
  debounceMs?: number;
}

interface UseAsyncSearchReturn<T> {
  results: T[];
  isLoading: boolean;
  search: string;
  setSearch: (query: string) => void;
  prefetch: () => void;
  hasError: boolean;
}

export function useAsyncSearch<T>({
  searchFn,
  debounceMs = 300,
}: UseAsyncSearchOptions<T>): UseAsyncSearchReturn<T> {
  const [results, setResults] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearchState] = useState('');
  const [hasError, setHasError] = useState(false);

  const abortRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastFetchedQuery = useRef<string | null>(null);
  const searchFnRef = useRef(searchFn);
  searchFnRef.current = searchFn;

  const doFetch = useCallback((query: string): void => {
    if (query === lastFetchedQuery.current) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    setHasError(false);

    searchFnRef
      .current(query, controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) {
          setResults(data);
          lastFetchedQuery.current = query;
          setIsLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (!controller.signal.aborted) {
          setHasError(true);
          setIsLoading(false);
        }
      });
  }, []);

  const setSearch = useCallback(
    (query: string): void => {
      setSearchState(query);
      if (timerRef.current != null) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => doFetch(query), debounceMs);
    },
    [doFetch, debounceMs]
  );

  const prefetch = useCallback((): void => {
    if (timerRef.current != null) clearTimeout(timerRef.current);
    doFetch('');
  }, [doFetch]);

  return { results, isLoading, search, setSearch, prefetch, hasError };
}
